import { z } from "zod";
import { LanguageCodeSchema } from "./utils";
import { SurveyResponseSchema, TranslationMapSchema } from "./surveys";
import {
  SurveyImportVisibleIfSchema,
  SurveyKeySchema,
} from "./surveyVisibility";

/**
 * Survey import file format ("procare-survey", version 1).
 *
 * A survey is authored as a single self-contained JSON file and uploaded from
 * the dashboard. This schema is the format's source of truth: it validates the
 * file server-side on upload and can be reused by the dashboard to check a
 * file before sending it.
 *
 * Design notes:
 * - Every human-readable string is a translation map ({ "en": "...", "fr": "..." }).
 *   If the file declares `languages`, every map must cover all of them.
 * - Questions carry a stable `key` (the instrument's variable ID, e.g.
 *   "PPE-15_Q01") used by display conditions and, later, research exports.
 * - Choice options carry an explicit numeric `value` — the instrument's
 *   response code. Values need not be contiguous (a survey may reserve codes,
 *   e.g. options 1,2,3,5). Display order is the array order.
 * - Question display order is the array order; `visibleIf` may only reference
 *   questions that appear EARLIER in the file (forward-only skip logic, no
 *   cycles by construction).
 * - `metadata` is free-form provenance (source instrument, scoring notes…).
 *   It is accepted anywhere and dropped by the importer, with ONE exception:
 *   the file-level `metadata.analysis.plugin` binds this questionnaire to a
 *   scoring plugin and is persisted as `Survey.analysisPluginId`. Read it with
 *   `analysisPluginOf()` rather than reaching into the record.
 */

export const SURVEY_IMPORT_FORMAT = "procare-survey" as const;
export const SURVEY_IMPORT_VERSION = 1 as const;

const MetadataSchema = z.record(z.string(), z.unknown());

/**
 * The key format and the display conditions live in `surveyVisibility.ts`,
 * beside the evaluator that reads them — the patient app and the API both need
 * the rule, and neither can depend on this file (the whole import format) to
 * get it. Re-exported through the package barrel from there, so
 * `SurveyImportConditionSchema` and `SurveyImportVisibleIfSchema` are the same
 * names they always were to anything outside the package.
 */
const KeySchema = SurveyKeySchema;

// ---------------------------------------------------------------------------
// Domains, options, questions
// ---------------------------------------------------------------------------

export const SurveyImportDomainSchema = z.object({
  key: KeySchema,
  text: TranslationMapSchema,
  metadata: MetadataSchema.optional(),
});
export type SurveyImportDomain = z.infer<typeof SurveyImportDomainSchema>;

export const SurveyImportOptionSchema = z.object({
  /** The instrument's response code — referenced by `visibleIf` and scoring. */
  value: z.number().int(),
  text: TranslationMapSchema,
  metadata: MetadataSchema.optional(),
});
export type SurveyImportOption = z.infer<typeof SurveyImportOptionSchema>;

const QuestionBaseSchema = z.object({
  key: KeySchema,
  /** Must match the `key` of a domain declared in `domains`. */
  domain: KeySchema,
  text: TranslationMapSchema,
  help: TranslationMapSchema.optional(),
  visibleIf: SurveyImportVisibleIfSchema.optional(),
  metadata: MetadataSchema.optional(),
});

export const SurveyImportSingleQuestionSchema = QuestionBaseSchema.extend({
  type: z.literal("SINGLE"),
  options: z.array(SurveyImportOptionSchema).min(2),
});
export const SurveyImportMultipleQuestionSchema = QuestionBaseSchema.extend({
  type: z.literal("MULTIPLE"),
  options: z.array(SurveyImportOptionSchema).min(2),
});
/**
 * A label for one point on a scale, shown above the track.
 *
 * DISPLAY ONLY, and that is the whole distinction from an option: an anchor is
 * never an answer, so it is not stored as one and cannot be selected. A 1-to-7
 * scale reading "never" at 1 and "always" at 7 is unanswerable without them —
 * the paper form prints the words, and a patient handed seven bare numbers is
 * being asked to guess which end is good.
 */
export const SurveyImportAnchorSchema = z.object({
  /** A point on the scale, within [minValue, maxValue]. */
  value: z.number().int(),
  text: TranslationMapSchema,
});
export type SurveyImportAnchor = z.infer<typeof SurveyImportAnchorSchema>;

export const SurveyImportRangeQuestionSchema = QuestionBaseSchema.extend({
  type: z.literal("RANGE"),
  minValue: z.number().int(),
  maxValue: z.number().int(),
  /**
   * Labels for points on the track. Usually the two ends; the Kidney PREM's
   * COVID item labels its MIDDLE too (-3 much worse, 0 stayed the same,
   * +3 much better), which is why this is a list of points rather than a
   * `lowLabel`/`highLabel` pair.
   */
  anchors: z.array(SurveyImportAnchorSchema).optional(),
  /**
   * Answers that are NOT points on the scale: "Don't know", "Not applicable".
   *
   * Real answers with real response codes, so they are options in every sense
   * except that they sit off the axis and render as buttons rather than as
   * positions on it. Their codes must fall OUTSIDE [minValue, maxValue] — a
   * scoring plugin tells them apart by code alone (the Kidney PREM drops 8 and
   * 9 from its means), and one that collided with a scale point would be
   * indistinguishable from a real measurement.
   */
  secondaryOptions: z.array(SurveyImportOptionSchema).optional(),
});

export const SurveyImportQuestionSchema = z.discriminatedUnion("type", [
  SurveyImportSingleQuestionSchema,
  SurveyImportMultipleQuestionSchema,
  SurveyImportRangeQuestionSchema,
]);
export type SurveyImportQuestion = z.infer<typeof SurveyImportQuestionSchema>;

// ---------------------------------------------------------------------------
// File
// ---------------------------------------------------------------------------

/**
 * Exported for `schemas:emit` only. Refinements do not survive
 * `z.toJSONSchema`, so the emitted `procare-survey.schema.json` is the
 * STRUCTURE of an import file and not its cross-field rules — which is exactly
 * what a plugin author needs while writing one. `SurveyImportFileSchema` below
 * stays the gate anything actually imported goes through.
 */
export const SurveyImportFileBaseSchema = z.object({
  format: z.literal(SURVEY_IMPORT_FORMAT),
  version: z.literal(SURVEY_IMPORT_VERSION),
  title: TranslationMapSchema,
  description: TranslationMapSchema,
  /**
   * The languages this survey supports. Required: every translation map in
   * the file must contain all of them, or the file is rejected — an
   * incompletely translated instrument fails at import time, not silently at
   * display time. Extra translations beyond the declared set are allowed.
   */
  languages: z.array(LanguageCodeSchema).min(1),
  domains: z.array(SurveyImportDomainSchema).min(1),
  questions: z.array(SurveyImportQuestionSchema).min(1),
  metadata: MetadataSchema.optional(),
});

/** Cross-field rules that plain shapes can't express. */
export const SurveyImportFileSchema = SurveyImportFileBaseSchema.superRefine(
  (file, ctx) => {
    const addIssue = (path: (string | number)[], message: string) =>
      ctx.addIssue({ code: "custom", path, message });

    // Unique domain keys
    const domainKeys = new Set<string>();
    file.domains.forEach((d, i) => {
      if (domainKeys.has(d.key))
        addIssue(["domains", i, "key"], `Duplicate domain key "${d.key}"`);
      domainKeys.add(d.key);
    });

    // Unique question keys, domains resolve, option values unique per question
    const questionByKey = new Map<string, SurveyImportQuestion>();
    file.questions.forEach((q, i) => {
      if (questionByKey.has(q.key))
        addIssue(["questions", i, "key"], `Duplicate question key "${q.key}"`);
      if (!domainKeys.has(q.domain))
        addIssue(
          ["questions", i, "domain"],
          `Unknown domain "${q.domain}" — declare it in "domains"`,
        );
      if (q.type === "RANGE") {
        if (q.minValue > q.maxValue)
          addIssue(["questions", i, "minValue"], "minValue must be <= maxValue");

        // An anchor labels a point that exists, and labels it once. One off the
        // end of the track has nowhere to render; two on the same point are a
        // scale that contradicts itself.
        const anchored = new Set<number>();
        (q.anchors ?? []).forEach((a, j) => {
          const path = ["questions", i, "anchors", j, "value"];
          if (a.value < q.minValue || a.value > q.maxValue)
            addIssue(
              path,
              `Anchor ${a.value} is outside the scale [${q.minValue}, ${q.maxValue}]`,
            );
          if (anchored.has(a.value))
            addIssue(path, `Duplicate anchor at ${a.value}`);
          anchored.add(a.value);
        });

        // A secondary answer is told apart from a real measurement by its CODE
        // and nothing else: a plugin drops "not applicable" from a mean by
        // excluding 9. A code that also names a point on the scale would make
        // the two indistinguishable in the stored data, which is silent and
        // unrecoverable.
        const secondary = new Set<number>();
        (q.secondaryOptions ?? []).forEach((o, j) => {
          const path = ["questions", i, "secondaryOptions", j, "value"];
          if (o.value >= q.minValue && o.value <= q.maxValue)
            addIssue(
              path,
              `Secondary option ${o.value} collides with the scale [${q.minValue}, ${q.maxValue}] — it must be off it`,
            );
          if (secondary.has(o.value))
            addIssue(path, `Duplicate secondary option value ${o.value}`);
          secondary.add(o.value);
        });
      }
      if (q.type === "SINGLE" || q.type === "MULTIPLE") {
        const values = new Set<number>();
        q.options.forEach((o, j) => {
          if (values.has(o.value))
            addIssue(
              ["questions", i, "options", j, "value"],
              `Duplicate option value ${o.value}`,
            );
          values.add(o.value);
        });
      }

      // visibleIf: forward-only references, type-compatible operators,
      // values that exist on the referenced question.
      const conditions =
        q.visibleIf === undefined
          ? []
          : "all" in q.visibleIf
            ? q.visibleIf.all
            : "any" in q.visibleIf
              ? q.visibleIf.any
              : [q.visibleIf];
      for (const cond of conditions) {
        const path = ["questions", i, "visibleIf"];
        const target = questionByKey.get(cond.question);
        if (!target) {
          addIssue(
            path,
            `visibleIf references "${cond.question}", which is not an earlier question in the file`,
          );
          continue;
        }
        if (target.type === "MULTIPLE") {
          addIssue(
            path,
            `visibleIf cannot reference MULTIPLE question "${cond.question}" (not supported in version 1)`,
          );
          continue;
        }
        if (
          (cond.op === "gte" || cond.op === "lte") &&
          target.type !== "RANGE"
        ) {
          addIssue(
            path,
            `Operator "${cond.op}" requires a RANGE question, but "${cond.question}" is ${target.type}`,
          );
          continue;
        }
        if (target.type === "SINGLE") {
          const optionValues = new Set(target.options.map((o) => o.value));
          const referenced = cond.op === "in" ? cond.values : [cond.value];
          for (const v of referenced) {
            if (!optionValues.has(v))
              addIssue(
                path,
                `Value ${v} is not an option value of question "${cond.question}"`,
              );
          }
        }
        if (target.type === "RANGE") {
          const secondary = target.secondaryOptions ?? [];

          // A magnitude comparison over a scale that can also answer "not
          // applicable" is a comparison against a code that is not on the
          // scale: 9 satisfies `gte 7` while meaning the opposite of a high
          // score. Refused rather than special-cased, because the correct
          // reading depends on the instrument and nobody would notice the
          // wrong one.
          if ((cond.op === "gte" || cond.op === "lte") && secondary.length > 0)
            addIssue(
              path,
              `Operator "${cond.op}" cannot reference "${cond.question}": it declares secondary options, whose codes are off its scale`,
            );

          // `eq` / `neq` / `in` against a RANGE mean either a point on the
          // scale or one of its secondary codes. Anything else can never match.
          if (cond.op === "eq" || cond.op === "neq" || cond.op === "in") {
            const secondaryValues = new Set(secondary.map((o) => o.value));
            const referenced = cond.op === "in" ? cond.values : [cond.value];
            for (const v of referenced) {
              const onScale = v >= target.minValue && v <= target.maxValue;
              if (!onScale && !secondaryValues.has(v))
                addIssue(
                  path,
                  `Value ${v} is neither on "${cond.question}"'s scale [${target.minValue}, ${target.maxValue}] nor one of its secondary options`,
                );
            }
          }
        }
      }

      // Register AFTER checking visibleIf so self-references are rejected.
      questionByKey.set(q.key, q);
    });

    // Every declared language must be covered by every translation map.
    const missing = (map: Record<string, string>) =>
      file.languages.filter((lang) => !(lang in map));
    const check = (
      map: Record<string, string> | undefined,
      path: (string | number)[],
    ) => {
      if (!map) return;
      for (const lang of missing(map))
        addIssue(path, `Missing "${lang}" translation`);
    };

    check(file.title, ["title"]);
    check(file.description, ["description"]);
    file.domains.forEach((d, i) => check(d.text, ["domains", i, "text"]));
    file.questions.forEach((q, i) => {
      check(q.text, ["questions", i, "text"]);
      check(q.help, ["questions", i, "help"]);
      if (q.type === "SINGLE" || q.type === "MULTIPLE")
        q.options.forEach((o, j) =>
          check(o.text, ["questions", i, "options", j, "text"]),
        );
      if (q.type === "RANGE") {
        // An untranslated anchor is an unlabelled end of a scale, which is the
        // one piece of text a numeric question cannot be answered without.
        (q.anchors ?? []).forEach((a, j) =>
          check(a.text, ["questions", i, "anchors", j, "text"]),
        );
        (q.secondaryOptions ?? []).forEach((o, j) =>
          check(o.text, ["questions", i, "secondaryOptions", j, "text"]),
        );
      }
    });
  },
);
export type SurveyImportFile = z.infer<typeof SurveyImportFileSchema>;

/**
 * The one part of `metadata` the system reads: which Python plugin scores this
 * questionnaire.
 *
 * It lives under `metadata` rather than as a top-level field because it is
 * provenance about the instrument, not part of its structure — a file with no
 * binding is a perfectly good questionnaire that simply is not scored.
 * Validated narrowly here so nothing else has to reach into an
 * `unknown`-valued record.
 */
export const SurveyImportAnalysisBindingSchema = z.object({
  plugin: z.string().min(1).max(64),
});

/**
 * The plugin id declared by a file, or null. Returns null (rather than
 * throwing) for a malformed binding: `metadata` is free-form by contract, so
 * something that does not look like a binding is not one.
 */
export function analysisPluginOf(file: SurveyImportFile): string | null {
  const parsed = SurveyImportAnalysisBindingSchema.safeParse(
    file.metadata?.analysis,
  );
  return parsed.success ? parsed.data.plugin : null;
}

export const importSurveySchemas = {
  body: SurveyImportFileSchema,
  response: { 201: SurveyResponseSchema },
};
